import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { allEventsActions } from './allEventsSlice';
import { selectAllEventsFilters, selectAllEventsCurrentPage, selectAllEventsPageSize, selectAllEventsTableId } from './allEventsSelectors';
import { tableControlActions } from 'store/tableControlSlice/tableControlSlice';
import { ensureColumnsConfig } from 'store/tableControlSlice/tableControlSagas';
import { axiosAiq } from 'config';
import { notificationHandler } from 'utils/utils';
import { transactionTableConfig, getTableConfigByTableId } from 'store/tableControlSlice/transactionTablesConfig';

const DEFAULT_TABLE_ID = transactionTableConfig.allLocationsTransactions.tableId;

// Resolve the active table config from the slice's tableId, falling back to All Events.
function* resolveActiveTableConfig() {
    const tableId = (yield select(selectAllEventsTableId)) || DEFAULT_TABLE_ID;
    const config = getTableConfigByTableId(tableId) || transactionTableConfig.allLocationsTransactions;
    return { tableId, config };
}

function* fetchFilterOptions() {
    try {
        yield put(allEventsActions.setFilterOptionsLoading(true));
        const response = yield axiosAiq.get('/v2/transactions/trucks/recent/filter-options');
        yield put(allEventsActions.setFilterOptions(response.data));
    } catch (err) {
        yield call(notificationHandler, { err, title: 'Error loading filter options' });
    } finally {
        yield put(allEventsActions.setFilterOptionsLoading(false));
    }
}

function* searchAllEvents() {
    try {
        yield put(allEventsActions.setSearchLoading(true));

        const { tableId, config } = yield call(resolveActiveTableConfig);
        const { configName, configUrlPath } = config;

        yield put(tableControlActions.fetchTableDataStart({ tableId }));

        // Fetch columns config once if not already loaded. These tables span locations, so no
        // locationId is passed and they keep the default captions.
        yield call(ensureColumnsConfig, configUrlPath(), configName);

        const filters = yield select(selectAllEventsFilters);
        const page = yield select(selectAllEventsCurrentPage);
        const size = yield select(selectAllEventsPageSize);

        // Branch the request based on the active table: All Events uses a POST
        // search endpoint, Manual Departures (announcements) uses a GET endpoint.
        let response;
        if (config.searchMethod === 'get') {
            const url = config.searchUrlPath(page, size);
            response = yield axiosAiq.get(url);
        } else {
            const body = {
                searchTerm: filters.searchTerm?.trim() || null,
                day: filters.day,
                locationIds: filters.locationIds?.length ? filters.locationIds : null,
                accountIds: filters.accountIds?.length ? filters.accountIds : null,
            };
            response = yield axiosAiq.post(`/v2/transactions/trucks/recent?page=${page}&size=${size}`, body);
        }

        // Normalize response: All Events returns `{ items, totalItems }`, the
        // announcements endpoint returns a plain array of rows.
        const payload = response.data;
        const items = Array.isArray(payload) ? payload : (payload?.items ?? []);
        const totalItems = Array.isArray(payload) ? payload.length : (payload?.totalItems ?? items.length);

        yield put(
            tableControlActions.fetchTableDataSuccess({
                data: items,
                tableId,
                pageNum: 0,
                append: false,
            })
        );
        yield put(allEventsActions.setTotalItems(totalItems));
    } catch (err) {
        yield put(tableControlActions.fetchTableDataFailed(err?.message || 'Search failed'));
        yield call(notificationHandler, { err, title: 'Error searching events' });
    } finally {
        yield put(allEventsActions.setSearchLoading(false));
    }
}

function* onFetchFilterOptions() {
    yield takeLatest(allEventsActions.fetchFilterOptionsStart.type, fetchFilterOptions);
}

function* onSearchStart() {
    yield takeLatest(allEventsActions.searchStart.type, searchAllEvents);
}

export function* allEventsSaga() {
    yield all([call(onFetchFilterOptions), call(onSearchStart)]);
}
